/**
 * SourceWithAgentSheet — right-side Sheet hosting the chat-style sourcing agent.
 *
 * Flow: the recruiter describes the need (PromptBar) → the agent streams its
 * reasoning (ReasoningTimeline) → external PREVIEWS render (AgentResultRow). Each
 * preview can be "unlocked" (a paid lookup) which reveals the full profile and
 * adds it to the talent pool, reusable across jobs. Scoring happens later, when
 * the talent is assigned to a job through the existing pool flow.
 *
 * No hardcoded strings, no gradients, no emojis.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';

import { Sheet } from '../Sheet/Sheet';
import { Typography } from '../../atoms/Typography';
import { Skeleton } from '../../atoms/Skeleton';
import { Button } from '@/components/ui/atoms/Button';
import { PromptBar, type ChipKey } from './PromptBar';
import { ReasoningTimeline } from './ReasoningTimeline';
import { AgentResultRow } from './AgentResultRow';
import { useSourcingAgent } from './useSourcingAgent';
import type { AgentCandidate } from './types';

export interface SourceWithAgentSheetProps {
  open: boolean;
  jobId: string;
  onClose: () => void;
  /** Called after a profile is unlocked into the pool, so lists can refresh. */
  onAdded?: () => void;
}

/** Map a backend `detail` code to a localized message key. */
function errorKey(code?: string) {
  switch (code) {
    case 'search_quota_exceeded':
      return 'sourcing.err_search_quota';
    case 'unlock_quota_exceeded':
      return 'sourcing.err_unlock_quota';
    case 'provider_rate_limited':
      return 'sourcing.err_provider_rate';
    case 'lookup_timeout':
      return 'sourcing.err_lookup_timeout';
    case 'lookup_failed':
      return 'sourcing.err_lookup_failed';
    default:
      return 'sourcing.error';
  }
}

/** Placeholder row matching the preview row's layout, so nothing shifts. */
const PreviewRowSkeleton: React.FC = () => (
  <div className="flex items-center gap-3 border-b border-light-lila/30 px-3 py-3 last:border-0">
    <Skeleton className="h-9 w-9 rounded-full shrink-0" />
    <div className="flex flex-1 flex-col gap-1.5">
      <Skeleton className="h-3.5 w-40" />
      <Skeleton className="h-3 w-28" />
    </div>
    <Skeleton className="h-8 w-20 rounded-full shrink-0" />
  </div>
);

export const SourceWithAgentSheet: React.FC<SourceWithAgentSheetProps> = ({
  open,
  jobId,
  onClose,
  onAdded,
}) => {
  const { t } = useTranslation('agents');
  const { state, run, unlock, reset } = useSourcingAgent(jobId);
  // The free-text search stays hidden until the recruiter opts to add details —
  // the default path is a single click that runs on the job's own criteria.
  const [showRefine, setShowRefine] = useState(false);

  const hasResults = state.candidates.length > 0;
  // While the agent has started searching but no previews have landed yet, show
  // skeleton rows so the panel feels alive and the layout is stable.
  const searching =
    state.phase === 'running' && !hasResults && (!!state.searchReasoning || state.steps.length > 0);

  const coverage = useMemo<Partial<Record<ChipKey, boolean>>>(() => {
    const chips = state.need?.chips ?? [];
    const has = (kinds: string[]) => chips.some((c) => kinds.includes(c.kind));
    return {
      job_title: !!state.need,
      location: has(['location', 'radius']),
      years_of_experience: has(['yoe']),
      skills: has(['skill']),
      industry: has(['industry']),
    };
  }, [state.need]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = (query: string) => {
    // The agent extracts every criterion from the free-text need itself.
    void run(query, { skills: [] });
  };

  const handleUnlock = useCallback(
    async (candidate: AgentCandidate) => {
      // Re-entrancy guard: ignore clicks while a profile is already unlocking or
      // already unlocked (the paid call must fire at most once per profile).
      const status = candidate.unlock?.status ?? 'locked';
      if (status === 'unlocking' || status === 'unlocked') return;
      try {
        const res = await unlock(candidate);
        const name = res.fullName || candidate.fullName || '';
        toast.success(
          res.cached
            ? t('sourcing.unlocked_cached_toast', { name })
            : t('sourcing.unlocked_toast', { name }),
        );
        onAdded?.();
      } catch (err) {
        const code = err instanceof Error ? err.message : undefined;
        toast.error(t(errorKey(code)));
      }
    },
    [unlock, onAdded, t],
  );

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) handleClose(); }} size="md">
      <Sheet.Header>
        <Sheet.Title>{t('sourcing.title')}</Sheet.Title>
      </Sheet.Header>

      <Sheet.Body className="flex flex-col gap-4 !py-4">
        <Typography variant="body-sm" className="text-dark-invirtus/70">
          {t('sourcing.subtitle')}
        </Typography>

        {showRefine ? (
          <div className="flex flex-col gap-2">
            <PromptBar
              disabled={state.phase === 'running'}
              coverage={coverage}
              onSubmit={handleSubmit}
            />
            <Button
              type="text"
              intent="muted"
              size="xs"
              onClick={() => setShowRefine(false)}
              disabled={state.phase === 'running'}
              label={t('sourcing.hide_details')}
              rightIcon={<ChevronUp />}
              className="self-center"
            />
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2.5">
            <Button
              type="contained"
              intent="primary"
              size="md"
              onClick={() => handleSubmit('')}
              disabled={state.phase === 'running'}
              icon={<Search />}
              label={t('sourcing.run')}
              className="w-full rounded-xl"
            />
            <Button
              type="text"
              intent="muted"
              size="xs"
              onClick={() => setShowRefine(true)}
              disabled={state.phase === 'running'}
              label={t('sourcing.add_details')}
              rightIcon={<ChevronDown />}
            />
          </div>
        )}

        {state.phase !== 'idle' && <ReasoningTimeline state={state} />}

        {state.phase === 'error' && (
          <Typography variant="body-sm" className="text-red">
            {t(errorKey(state.error))}
          </Typography>
        )}

        {searching && (
          <section>
            <div className="mb-2">
              <Typography variant="body-sm" className="font-semibold text-dark-invirtus/60">
                {t('sourcing.searching')}
              </Typography>
            </div>
            <div className="flex flex-col rounded-2xl border border-light-lila">
              {Array.from({ length: 4 }).map((_, i) => (
                <PreviewRowSkeleton key={i} />
              ))}
            </div>
          </section>
        )}

        {hasResults && (
          <section>
            <div className="mb-2 flex items-center justify-between">
              <Typography variant="body-sm" className="font-semibold">
                {t('sourcing.results_title')} ·{' '}
                {t('sourcing.results_count', { count: state.candidates.length })}
              </Typography>
              <Typography variant="caption" color="secondary">
                {t('sourcing.unlock_hint')}
              </Typography>
            </div>
            <div className="flex flex-col rounded-2xl border border-light-lila">
              {state.candidates.map((c) => (
                <AgentResultRow key={c.dedupKey} candidate={c} onUnlock={handleUnlock} />
              ))}
            </div>
          </section>
        )}

        {state.phase === 'done' && !hasResults && (
          <Typography variant="body-sm" color="secondary" className="py-6 text-center">
            {t('sourcing.empty')}
          </Typography>
        )}
      </Sheet.Body>
    </Sheet>
  );
};

SourceWithAgentSheet.displayName = 'SourceWithAgentSheet';
